import { useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { assetUrl, formatApiError, uploadImages } from "@/lib/api";
import { ImageDropzone } from "@/components/common/ImageDropzone";
import { TypeLineBuilder } from "@/components/cards/TypeLineBuilder";

export function FaceImagePicker({ face, onChange, label, testid, natural = false }) {
  const box = natural ? "h-56" : "aspect-[63/88]";
  const [busy, setBusy] = useState(false);
  const upload = async ([file]) => {
    setBusy(true);
    try {
      const [res] = await uploadImages([file], "card_image");
      if (!res.ok) throw new Error(res.error);
      onChange({ image_file_id: res.file_id, image_url: res.url, width: res.width, height: res.height });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  if (busy)
    return <div className={`flex ${box} items-center justify-center rounded-xl border border-amber-900/40 bg-[#121620]`}><Loader2 className="h-6 w-6 animate-spin text-amber-400" /></div>;
  if (face.image_url)
    return (
      <div className="space-y-2">
        <img src={assetUrl(face.image_url)} alt={`${label} preview`} className={natural ? "mx-auto block h-auto max-h-[70vh] max-w-full rounded-xl border border-amber-900/40" : "aspect-[63/88] w-full rounded-xl border border-amber-900/40 object-cover"} data-testid={`${testid}-preview`} />
        <label className="btn-ghost w-full cursor-pointer">
          <RefreshCw className="h-4 w-4" />Replace image
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid={`${testid}-replace-input`}
            onChange={(e) => e.target.files?.[0] && upload([e.target.files[0]])} />
        </label>
      </div>
    );
  return <div className={box}><ImageDropzone onFiles={upload} label={label} testid={testid} compact /></div>;
}

const TEXT_FIELDS = [
  { key: "name", label: "Card name", required: true },
  { key: "mana_cost", label: "Mana cost", placeholder: "{2}{U}{U}" },
  { key: "type_line", label: "Type line", placeholder: "Creature — Merfolk Wizard", wide: true },
];
const STAT_FIELDS = [{ key: "power", label: "Power" }, { key: "toughness", label: "Toughness" }, { key: "loyalty", label: "Loyalty" }, { key: "defense", label: "Defense" }];

export function FaceFields({ face, onChange, prefix }) {
  const set = (k) => (e) => onChange({ [k]: e.target.value });
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {TEXT_FIELDS.map((f) => (
        <div key={f.key} className={f.wide ? "sm:col-span-2" : ""}>
          <label className="field-label" htmlFor={`${prefix}-${f.key}`}>{f.label}</label>
          <input id={`${prefix}-${f.key}`} className="field" required={f.required} placeholder={f.placeholder} value={face[f.key] || ""}
            onChange={set(f.key)} data-testid={`${prefix}-${f.key.replace("_", "-")}-input`} />
        </div>
      ))}
      <div className="sm:col-span-2">
        <TypeLineBuilder value={face.type_line || ""} onChange={(type_line) => onChange({ type_line })} prefix={prefix} />
      </div>
      <div className="sm:col-span-2">
        <label className="field-label" htmlFor={`${prefix}-oracle`}>Abilities / rules text</label>
        <textarea id={`${prefix}-oracle`} rows={4} className="field" value={face.oracle_text || ""} onChange={set("oracle_text")} data-testid={`${prefix}-oracle-text-input`} />
      </div>
      <div className="sm:col-span-2">
        <label className="field-label" htmlFor={`${prefix}-flavor`}>Flavor text</label>
        <textarea id={`${prefix}-flavor`} rows={2} className="field italic" value={face.flavor_text || ""} onChange={set("flavor_text")} data-testid={`${prefix}-flavor-text-input`} />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:col-span-2 sm:grid-cols-4">
        {STAT_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="field-label" htmlFor={`${prefix}-${f.key}`}>{f.label}</label>
            <input id={`${prefix}-${f.key}`} className="field" maxLength={10} value={face[f.key] || ""} onChange={set(f.key)} data-testid={`${prefix}-${f.key}-input`} />
          </div>
        ))}
      </div>
      <div className="sm:col-span-2">
        <label className="field-label" htmlFor={`${prefix}-artist`}>Artist / art credit</label>
        <input id={`${prefix}-artist`} className="field" value={face.artist || ""} onChange={set("artist")} data-testid={`${prefix}-artist-input`} />
      </div>
    </div>
  );
}
